import React, { createContext, useContext, useState, useCallback } from 'react';

type SaveOrderHandler = () => void | Promise<void>;

interface OrderActionsContextType {
  triggerSaveOrder?: () => Promise<void>;
  setSaveOrderHandler: (handler: SaveOrderHandler | null) => void;
  isSavingOrder: boolean;
  setSavingOrder: (saving: boolean) => void;
}

const OrderActionsContext = createContext<OrderActionsContextType | undefined>(undefined);

export const useOrderActions = () => {
  const context = useContext(OrderActionsContext);
  if (!context) {
    throw new Error('useOrderActions must be used within an OrderActionsProvider');
  }
  return context;
};

interface OrderActionsProviderProps {
  children: React.ReactNode;
}

export const OrderActionsProvider: React.FC<OrderActionsProviderProps> = ({ children }) => {
  const [saveOrderHandler, setSaveOrderHandlerState] = useState<SaveOrderHandler | null>(null);
  const [isSavingOrder, setSavingOrder] = useState(false);

  const setSaveOrderHandler = useCallback((handler: SaveOrderHandler | null) => {
    setSaveOrderHandlerState(() => handler);
  }, []);

  const triggerSaveOrder = useCallback(async () => {
    if (saveOrderHandler) {
      await saveOrderHandler();
    } else {
      console.warn("Save order handler not set.");
    }
  }, [saveOrderHandler]);

  return (
    <OrderActionsContext.Provider value={{ triggerSaveOrder, setSaveOrderHandler, isSavingOrder, setSavingOrder }}>
      {children}
    </OrderActionsContext.Provider>
  );
};
